import React, { useMemo, useState } from "react";

import { faMagnifyingGlass, faPlus } from "@performyard/fontawesome/pro-regular-svg-icons";
import { ButtonStandard, Icon, Text } from "@performyard/py-components";

import ChecklistItem from "./ChecklistItem";
import ChecklistSection from "./ChecklistSection";

import type { ItemStatus } from "../types";

import {
	useAddMeetingAgendaItem,
	useEditMeetingAgendaItem,
	useMeetingAgendaItems,
	useSearchService,
} from "../../../../api/hooks";
import { useChecklistItems, useGoalActionModals } from "../hooks";

import { SERVICE_GOAL } from "../../../../api/services/defs/search-service";
import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import { config } from "../config";
import { useMeetingContext } from "../context";
import { AddGoalsToAgendaModal } from "../modals";

/**
 * Agenda items section with the ability to add, edit, and delete agenda items.
 */
const AgendaItemsSection = (): React.ReactElement => {
	const { meeting, isInteractionDisabled, userId, isManagerDirectReportMeeting } = useMeetingContext();
	const { data: items, isLoading } = useMeetingAgendaItems(meeting._id);
	const { mutate: addItem } = useAddMeetingAgendaItem();
	const { mutate: editItem } = useEditMeetingAgendaItem();
	const [isAddGoalsOpen, setIsAddGoalsOpen] = useState(false);

	const {
		activeItems,
		isAddingItem,
		newItemName,
		editingItemId,
		editingItemCurrentValue,
		handleEditStart,
		handleEditStop,
		handleNameChange,
		handleNewItemNameChange,
		handleStatusChange,
		handleAddButtonClick,
		handleNewItemSave,
	} = useChecklistItems({
		meetingId: meeting._id,
		itemType: "agenda",
		items,
		isLoading,
		addItemMutation: addItem,
		editItemMutation: editItem,
		isInteractionDisabled,
	});

	const isAddButtonDisabled =
		isInteractionDisabled || editingItemId !== null || (isAddingItem && !newItemName.trim());
	const agendaGoalIds = useMemo(() => (items || []).map((item) => item.goalId).filter(Boolean) as string[], [items]);
	const goalsQuery = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: { _id_in: agendaGoalIds },
		limit: agendaGoalIds.length || config.goals.listLimit,
		defaultErrorMessage: "There was an error getting goals",
		disabled: agendaGoalIds.length === 0,
	});
	const goalById = useMemo(
		() => new Map((goalsQuery.data?.results || []).map((goal) => [goal._id, goal])),
		[goalsQuery.data?.results]
	);
	const { handleActionSelect: handleGoalActionSelect, modals } = useGoalActionModals({
		onGoalUpdated: () => goalsQuery.refetch(),
	});

	const addButton = !isInteractionDisabled ? (
		<div className="flex flex-wrap gap-2">
			<ButtonStandard
				iconLeft={faPlus}
				onClick={handleAddButtonClick}
				disabled={isAddButtonDisabled}
				dangerouslySetClassName={
					isAddButtonDisabled
						? "disabled:cursor-not-allowed disabled:pointer-events-auto w-fit flex-shrink-0"
						: "w-fit flex-shrink-0"
				}
			>
				Add Agenda Item
			</ButtonStandard>
			{isManagerDirectReportMeeting ? (
				<ButtonStandard
					iconLeft={faPlus}
					variant="outline"
					color="neutral"
					onClick={() => setIsAddGoalsOpen(true)}
					disabled={isInteractionDisabled}
					dangerouslySetClassName="w-fit flex-shrink-0"
				>
					Add Goal
				</ButtonStandard>
			) : null}
		</div>
	) : undefined;

	return (
		<>
			<ChecklistSection
				title="Agenda"
				isLoading={isLoading}
				isCollapsible={true}
				errorMessage="Failed to load agenda items"
				variant="sidebar"
				footer={addButton}
			>
				{activeItems && activeItems.length > 0
					? activeItems.map((item) => {
							const meetingItem = item.meetings.find((m) => m.meetingId === meeting._id);

							return (
								<ChecklistItem
									key={item._id}
									id={item._id}
									name={item.name}
									status={(meetingItem?.status as ItemStatus) || "incomplete"}
									creatorId={item.created?.userId || ""}
									userId={userId}
									isEditing={editingItemId === item._id}
									meetingId={meeting._id}
									itemType="agenda"
									onEdit={handleEditStart}
									onNameChange={handleNameChange}
									onStatusChange={(status) => handleStatusChange(item._id, status)}
									onSave={handleEditStop}
									currentEditValue={editingItemCurrentValue}
									{...(item.goalId
										? {
												goalId: item.goalId,
												goal: goalById.get(item.goalId),
												onGoalActionSelect: handleGoalActionSelect,
											}
										: {})}
								/>
							);
						})
					: !isAddingItem && (
							<div className="flex flex-col items-center justify-center gap-[9px] px-4 pb-10 pt-16 text-center">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-1">
									<Icon icon={faMagnifyingGlass} size={config.iconSize.large} color="neutral-7" />
								</div>
								<Text size="paragraph-2" weight="semi-bold" color="neutral-9">
									No Agenda Items
								</Text>
							</div>
						)}

				{isAddingItem && (
					<ChecklistItem
						id="new-agenda-item"
						name=""
						status="incomplete"
						creatorId={userId}
						userId={userId}
						isEditing={true}
						isNewItem={true}
						itemType="agenda"
						onEdit={() => {}}
						onNameChange={handleNewItemNameChange}
						onStatusChange={() => {}}
						onSave={handleNewItemSave}
						currentEditValue={newItemName}
					/>
				)}
			</ChecklistSection>
			<AddGoalsToAgendaModal
				isOpen={isAddGoalsOpen}
				onClose={() => setIsAddGoalsOpen(false)}
				existingGoalIds={agendaGoalIds}
			/>
			{modals}
		</>
	);
};

export default AgendaItemsSection;
